// src/pages/Home.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Shield, Waves, Users, Wind, Eye, ArrowUp, Anchor } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import logoLight from "../assets/antaryatra-logo-horizontal 3.0.svg";
import SEO from "../components/SEO";
import WisdomSection from "../components/Home/WisdomSection";
import VentBox from "../components/Sanctuary/VentBox";
import TheLivingBreath from "../components/Home/TheLivingBreath";
import PresencePulse from "../components/Home/PresencePulse";
import SanctuaryGlimpse from "../components/Home/SanctuaryGlimpse";
import { useAuth } from "../context/AuthContext";


const fadeInOnly = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.8 } }
};

const slideUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

// Reusable Section Component
const Section = ({ children, className = "" }) => (
  <section className={`py-20 md:py-28 px-6 md:px-12 max-w-7xl mx-auto ${className}`}>
    {children}
  </section>
);

export default function Home() {
  const { t, language, switchLanguage } = useLanguage();
  const { user } = useAuth();
  const startLink = "/signup";
  const [isMuted, setIsMuted] = useState(true);
  const [showTopBtn, setShowTopBtn] = useState(false);
  const [showVent, setShowVent] = useState(false);
  const videoRef = React.useRef(null);
  const mobileVideoRef = React.useRef(null);

  const heroVideoUrl = "/assets/videos/hero-desktop.mp4";
  const heroMobileVideoUrl = "/assets/videos/hero-mobile.mp4";

  const toggleAudio = () => {
    if (videoRef.current) videoRef.current.muted = !videoRef.current.muted;
    if (mobileVideoRef.current) mobileVideoRef.current.muted = !mobileVideoRef.current.muted;
    setIsMuted(!isMuted);
  };

  // Mute video when VentBox is open
  useEffect(() => {
    if (showVent) {
        if (videoRef.current) videoRef.current.muted = true;
        if (mobileVideoRef.current) mobileVideoRef.current.muted = true;
    } else {
        if (videoRef.current) videoRef.current.muted = isMuted;
        if (mobileVideoRef.current) mobileVideoRef.current.muted = isMuted;
    }
  }, [showVent, isMuted]);

  // Scroll to top logic
  useEffect(() => {
    const handleScroll = () => {
      setShowTopBtn(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 font-body selection:bg-brand-blue/20 relative">
      <SEO />
      {/* 1️⃣ HERO SECTION WITH VIDEO BACKGROUND */}
      <section className="relative overflow-hidden">
        {/* DESKTOP HERO - Immersive Full Screen */}
        <div className="hidden md:flex relative h-screen min-h-[600px] flex-col items-center justify-end text-center px-6 pb-48 overflow-hidden">
          {/* Video Background - DESKTOP */}
          <div className="absolute inset-0 z-0">
            <video
              ref={videoRef}
              autoPlay
              loop
              muted={isMuted}
              playsInline
              preload="auto"
              className="w-full h-full min-w-full min-h-full object-cover object-center"
              poster={logoLight}
            >
              <source src={heroVideoUrl} type="video/mp4" />
            </video>
          </div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={slideUp}
            className="relative z-10 max-w-5xl space-y-12"
          >
            <motion.div 
              className="space-y-6"
              animate={{
                scale: [1, 1.015, 1],
                opacity: [0.6, 1, 0.6],
                letterSpacing: ["-0.02em", "0.02em", "-0.02em"]
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <h1 
                className="text-2xl md:text-4xl font-display font-medium text-white leading-relaxed max-w-3xl mx-auto px-6"
                style={{ transform: 'translateY(25px)' }}
              >
                {t('hero_headline')}
              </h1>
              <p 
                className="text-base md:text-lg text-white/70 font-display font-light max-w-xl mx-auto px-8"
                style={{ transform: 'translateY(40px)' }}
              >
                {t('hero_subheadline')}
              </p>
            </motion.div>

          </motion.div>
        </div>

        {/* MOBILE HERO - Video on top, content below (Overlaying on Staging) */}
        <div className="md:hidden flex flex-col bg-white dark:bg-gray-950 relative">
          <div className="relative w-full overflow-hidden bg-black">
            <video
              ref={mobileVideoRef}
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              className="w-full h-auto opacity-100 block"
            >
              <source src={heroMobileVideoUrl} type="video/mp4" />
            </video>

            {/* Text Framing for Mobile (Off-Video) */}
            <div className="absolute inset-0 z-10 flex flex-col justify-between py-24 pointer-events-none">
              {/* Upper Text */}
              <motion.div 
                className="px-8 text-center"
                animate={{
                  scale: [1, 1.015, 1],
                  opacity: [0.6, 1, 0.6],
                  letterSpacing: ["-0.01em", "0.02em", "-0.01em"]
                }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <h1 className="text-xl md:text-2xl font-display font-medium text-white leading-tight max-w-[280px] mx-auto">
                  {t('hero_headline')}
                </h1>
              </motion.div>

              {/* Lower Text */}
              <motion.div 
                className="px-8 text-center"
                animate={{
                  scale: [1, 1.015, 1],
                  opacity: [0.6, 1, 0.6],
                  letterSpacing: ["-0.01em", "0.02em", "-0.01em"]
                }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <p className="text-[10px] text-white/50 font-display font-light max-w-[240px] mx-auto uppercase tracking-[0.2em]">
                  {t('hero_subheadline')}
                </p>
              </motion.div>
            </div>

          </div>
        </div>

        {/* Audio Toggle Button */}
        <button
          onClick={toggleAudio}
          className="absolute top-24 right-6 z-20 p-2 rounded-full bg-black/20 hover:bg-black/40 text-white/70 hover:text-white transition-all backdrop-blur-sm border border-white/10"
          title={isMuted ? "Unmute Ambient Sound" : "Mute"}
        >
          {isMuted ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z" /><line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" /></svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path></svg>
          )}
        </button>

        {/* Global Language Toggle */}
        <div className="absolute top-6 right-6 z-30 flex items-center bg-black/40 backdrop-blur-md rounded-full border border-white/10 p-1">
          <button
            onClick={() => switchLanguage('en')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${language === 'en' ? 'bg-white text-gray-900 shadow-md' : 'text-white/70 hover:text-white'}`}
          >
            English
          </button>
          <button
            onClick={() => switchLanguage('hi')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${language === 'hi' ? 'bg-white text-gray-900 shadow-md' : 'text-white/70 hover:text-white'}`}
          >
            हिन्दी / Hinglish
          </button>
        </div>
      </section>

      {/* 1.2 SOFT ENTRY LINE (NEW) */}
      <section className="py-24 bg-white dark:bg-gray-950 px-6 text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInOnly} className="max-w-4xl mx-auto space-y-4">
          <h2 className="text-2xl md:text-3xl font-display font-medium text-gray-800 dark:text-gray-200">
            {t('soft_entry_title')}
          </h2>
          <p className="text-xl md:text-2xl font-serif italic text-gray-500 dark:text-gray-400">
            {t('soft_entry_body')}
          </p>

          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            animate={{
              scale: [1, 1.02, 1],
            }}
            transition={{ 
              initial: { delay: 0.5, duration: 0.8 },
              animate: { duration: 8, repeat: Infinity, ease: "easeInOut" }
            }}
            className="pt-16 flex flex-col items-center gap-8"
          >
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 w-full">
              {/* Primary CTA */}
              <button 
                onClick={() => setShowVent(true)}
                className="px-10 py-5 text-lg font-display font-medium rounded-full shadow-2xl shadow-gray-200 dark:shadow-none transition-all bg-white text-gray-900 border-none group flex items-center gap-4 hover:scale-105 active:scale-95"
              >
                <Wind className="w-5 h-5 text-brand-orange group-hover:rotate-12 transition-transform" />
                {t('hero_vent')}
              </button>
              
              {/* Secondary CTA */}
              {!user ? (
                <Link to="/signup" className="px-10 py-5 border-2 border-gray-100 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 hover:bg-gray-50 dark:hover:bg-white/5 rounded-full text-gray-700 dark:text-gray-300 transition-all text-lg font-medium group flex items-center gap-3">
                  {t('hero_cta')}
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              ) : (
                <Link to="/dashboard" className="px-10 py-5 border-2 border-gray-100 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 hover:bg-gray-50 dark:hover:bg-white/5 rounded-full text-gray-700 dark:text-gray-300 transition-all text-lg font-medium group flex items-center gap-3">
                  {t('nav_space')}
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              )}
            </div>

            {/* Shared Microcopy */}
            <p className="text-xs text-gray-400 dark:text-gray-500 font-light italic tracking-wide">
              {t('hero_nudge')}
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* 1.3 STATES SECTION (MOVED UP) */}
      <Section className="border-t border-gray-50 dark:border-gray-900">
        <h2 className="text-3xl md:text-4xl font-display font-medium text-center mb-16">{t('sec4_title')}</h2>
        <div className="grid md:grid-cols-3 gap-8 md:gap-y-12">
          {[
            { icon: <Wind className="w-6 h-6" />, title: t('sec4_state1'), desc: t('sec4_state1_desc') },
            { icon: <Shield className="w-6 h-6" />, title: t('sec4_state2'), desc: t('sec4_state2_desc') },
            { icon: <Waves className="w-6 h-6" />, title: t('sec4_state3'), desc: t('sec4_state3_desc') },
            { icon: <Eye className="w-6 h-6" />, title: t('sec4_state4'), desc: t('sec4_state4_desc') },
            { icon: <Heart className="w-6 h-6" />, title: t('sec4_state5'), desc: t('sec4_state5_desc') }
          ].map((card, idx) => (
            <motion.div
              key={idx}
              className="p-8 rounded-3xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-xl transition-all h-full flex flex-col items-start"
            >
              <div className="w-12 h-12 bg-blue-50 dark:bg-gray-700 text-brand-blue rounded-2xl flex items-center justify-center mb-6">
                {card.icon}
              </div>
              <h3 className="text-xl font-medium mb-3">{card.title}</h3>
              <p className="text-gray-500 dark:text-gray-400 leading-relaxed font-light">{card.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.div 
          initial="hidden" 
          whileInView="visible" 
          viewport={{ once: true }} 
          className="mt-20 text-center space-y-8"
        >
          <p className="text-xl text-gray-500 dark:text-gray-400 font-light italic">
            {t('sec4_nudge')}
          </p>
          <div className="pt-4">
            <Link to={startLink} className="inline-flex items-center gap-2 px-10 py-4 bg-brand-blue text-white rounded-full transition-all font-medium text-lg shadow-xl shadow-blue-900/10">
              {t('sec4_cta')} <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </motion.div>
      </Section>

      {/* 1.5 THE VIEW FROM WITHIN: Sanctuary Glimpse */}
      <section className="bg-white dark:bg-gray-950">
        <div className="text-center pt-24 pb-8">
            <h2 className="text-3xl font-display font-medium text-gray-900 dark:text-white mb-4">The View from Within</h2>
            <p className="text-gray-500 font-serif italic">"Witness the beauty of the quiet space."</p>
        </div>
        <SanctuaryGlimpse />
      </section>

      {/* 2️⃣ THE INVITATION: A PLACE TO BE */}
      <Section className="text-center space-y-12 border-t border-gray-50 dark:border-gray-900">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}>
          <h2 className="text-3xl md:text-5xl font-display font-medium mb-8">{t('sec2_title')}</h2>
          <div className="max-w-3xl mx-auto space-y-10 text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed font-light">
            <p>{t('sec2_sub')}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-12">
              <div className="p-6 rounded-2xl bg-gray-50 dark:bg-gray-900/50 text-gray-600 dark:text-gray-400 border border-gray-100 dark:border-gray-800 italic">
                {t('sec2_val_fix')}
              </div>
              <div className="p-6 rounded-2xl bg-gray-50 dark:bg-gray-900/50 text-gray-600 dark:text-gray-400 border border-gray-100 dark:border-gray-800 italic">
                {t('sec2_val_strength')}
              </div>
            </div>

            <p className="text-gray-500 font-serif italic text-base md:text-lg">
              {t('sec2_desc')}
            </p>
          </div>
        </motion.div>
      </Section>

      {/* 3️⃣ THE INTENTION BEHIND YOUR JOURNEY */}
      <Section className="bg-gradient-to-br from-gray-50/50 to-blue-50/30 dark:from-gray-900/50 dark:to-brand-blue/5 rounded-3xl my-12 border border-gray-100 dark:border-gray-800">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div className="space-y-8">
            <h2 className="text-3xl md:text-4xl font-display font-medium">{t('sec3_title')}</h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
              {t('sec3_sub')}
            </p>
            <p className="text-gray-500 dark:text-gray-400 font-light leading-relaxed">
              {t('sec3_desc')}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 p-10 rounded-2xl shadow-xl shadow-blue-900/5 border border-gray-100 dark:border-gray-700 relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-16 -mr-16 -mt-16 bg-blue-100/30 dark:bg-brand-blue/10 rounded-full opacity-40 blur-3xl group-hover:scale-110 transition-transform duration-1000" />
            <h3 className="text-xl md:text-2xl font-serif italic text-gray-800 dark:text-gray-200 leading-relaxed">
              {t('sec3_quote')}
            </h3>
            <div className="mt-8 space-y-3">
              <div className="h-1.5 w-3/4 bg-gray-100 dark:bg-gray-700 rounded-full" />
              <div className="h-1.5 w-1/2 bg-gray-100 dark:bg-gray-700 rounded-full" />
            </div>
          </div>
        </div>
      </Section>

      {/* 5️⃣ FOR THOSE WHO WALK BESIDE */}
      <section className="py-24 bg-stone-50 dark:bg-stone-900/40 border-y border-stone-200 dark:border-stone-800">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">

          {/* Visual */}
          <div className="relative order-2 md:order-1">
            <div className="absolute inset-0 bg-stone-200 dark:bg-stone-800 rounded-full blur-3xl opacity-30" />
            <div className="relative z-10 p-10 border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-950 rounded-3xl shadow-2xl">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 rounded-2xl bg-stone-100 dark:bg-stone-800 flex items-center justify-center text-stone-600 dark:text-stone-400 shadow-inner">
                  <Anchor className="w-6 h-6" />
                </div>
                <span className="text-xs uppercase tracking-widest text-stone-500 font-bold">{t('sec5_badge')}</span>
              </div>
              <p className="text-2xl font-serif italic text-stone-700 dark:text-stone-300 mb-6 leading-relaxed">
                {t('sec5_quote')}
              </p>
              <div className="flex gap-2">
                <div className="h-1 w-full bg-stone-100 dark:bg-stone-800 rounded-full" />
                <div className="h-1 w-1/4 bg-stone-400 dark:bg-stone-600 rounded-full" />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-10 order-1 md:order-2">
            <h2 className="text-4xl md:text-5xl font-display font-medium text-stone-900 dark:text-stone-100">
              {t('sec5_title')}
            </h2>
            <p className="text-xl text-stone-600 dark:text-stone-400 leading-relaxed font-light">
              {t('sec5_sub')}
            </p>
            <div className="pt-8 border-t border-stone-200 dark:border-stone-800">
              <p className="text-stone-500 dark:text-stone-500 font-serif italic text-sm mb-6">
                {t('sec5_guide_tagline')}
              </p>
              <Link to="/apply" className="inline-flex items-center gap-2 px-8 py-4 bg-stone-800 dark:bg-stone-100 text-white dark:text-stone-900 rounded-2xl hover:opacity-90 transition-all font-medium text-lg shadow-lg shadow-stone-900/10">
                {t('sec5_apply')} <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 5.5 TRUST LAYER (NEW) */}
      <section className="py-24 bg-white dark:bg-gray-950 px-6 text-center border-t border-gray-50 dark:border-gray-900">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="max-w-4xl mx-auto space-y-8">
          <h2 className="text-3xl md:text-4xl font-display font-medium text-gray-900 dark:text-white">
            {t('trust_title')}
          </h2>
          <p className="text-xl text-gray-500 dark:text-gray-400 font-light leading-relaxed max-w-2xl mx-auto">
            {t('trust_desc')}
          </p>
        </motion.div>
      </section>

      {/* 6️⃣ THE SANCTUARY'S ROOTS (Consolidated Mission & Vision) */}
      <div id="mission" className="bg-slate-950 text-white py-32 px-6 overflow-hidden relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-blue-900/20 via-transparent to-transparent opacity-50" />
        
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="max-w-5xl mx-auto space-y-20 relative z-10">
          <div className="text-center space-y-8">
            <h2 className="text-4xl md:text-6xl font-display font-medium tracking-tight">{t('sec6_title')}</h2>
            <p className="text-xl md:text-2xl text-blue-100/70 font-light leading-relaxed max-w-4xl mx-auto">
              {t('sec6_sub')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-16 items-start">
            {/* Mission Pillars */}
            <div className="space-y-8">
              <div className="p-8 bg-white/5 rounded-3xl backdrop-blur-xl border border-white/10 hover:bg-white/10 transition-colors group">
                <h4 className="text-xl font-medium mb-3 text-blue-200">{t('sec6_p1')}</h4>
                <p className="text-blue-100/60 leading-relaxed font-light">{t('sec6_p1_desc')}</p>
              </div>
              <div className="p-8 bg-white/5 rounded-3xl backdrop-blur-xl border border-white/10 hover:bg-white/10 transition-colors group">
                <h4 className="text-xl font-medium mb-3 text-blue-200">{t('sec6_p2')}</h4>
                <p className="text-blue-100/60 leading-relaxed font-light">{t('sec6_p2_desc')}</p>
              </div>
              <div className="p-8 bg-white/5 rounded-3xl backdrop-blur-xl border border-white/10 hover:bg-white/10 transition-colors group">
                <h4 className="text-xl font-medium mb-3 text-blue-200">{t('sec6_p3')}</h4>
                <p className="text-blue-100/60 leading-relaxed font-light">{t('sec6_p3_desc')}</p>
              </div>
            </div>

            {/* Vision Quote */}
            <div className="flex flex-col justify-center h-full pt-12 md:pt-0">
              <blockquote className="text-2xl md:text-3xl font-serif italic text-blue-50 leading-relaxed border-l-4 border-blue-500/30 pl-8">
                "{t('sec6_quote')}"
              </blockquote>
              <div className="mt-12 p-8 rounded-3xl bg-brand-blue/10 border border-brand-blue/20">
                <p className="text-blue-200 font-light leading-relaxed italic">
                  {t('sec6_desc')}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <WisdomSection />


      {/* 8️⃣ A QUIET PROMISE */}
      <div className="py-24 bg-gray-50 dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800 relative overflow-hidden">
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-500/10 to-transparent" />
        <div className="max-w-4xl mx-auto px-6 text-center space-y-12">
          <h2 className="text-3xl font-display font-serif italic text-gray-400">{t('sec8_title')}</h2>
          <p className="text-2xl md:text-4xl text-gray-800 dark:text-gray-100 font-light leading-relaxed">
            {t('sec8_promise')}
          </p>
          <p className="text-gray-500 dark:text-gray-400 text-lg font-light max-w-2xl mx-auto">
            {t('sec8_desc')}
          </p>

          <div className="pt-8 flex flex-col items-center gap-8">
            <div className="space-y-4">
              <Link to={startLink} className="inline-block px-12 py-5 bg-gray-900 dark:bg-brand-dark text-white rounded-full text-lg font-medium transition-all shadow-2xl">
                {t('sec8_cta')}
              </Link>
              <p className="text-gray-400 dark:text-gray-500 text-sm italic">
                {t('sec8_sub_cta')}
              </p>
            </div>
            
            <div className="pt-12">
              <button 
                onClick={() => setShowVent(true)}
                className="group flex items-center gap-3 text-gray-400 hover:text-brand-orange transition-colors"
              >
                <Wind className="w-4 h-4" />
                <span className="text-sm font-medium underline underline-offset-8 decoration-gray-200 dark:decoration-gray-800 group-hover:decoration-brand-orange transition-all">
                  {t('sec8_new_here_cta')}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 9️⃣ FOOTER */}
      <footer className="bg-white dark:bg-gray-950 border-t border-gray-100 dark:border-gray-800 py-16 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start gap-12">
          {/* Left: Logo + tagline */}
          <div className="space-y-4">
            <div className="flex flex-col items-start p-1 transition-opacity hover:opacity-80">
              <img src={logoLight} className="h-16 w-auto object-contain" alt="Antaryatra" />
            </div>
            <p className="text-gray-500 font-light italic text-base">{t('foot_tagline')}</p>
          </div>

          {/* Right: Nav links - Grouped by Intent */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-12 gap-y-8 text-sm">
            <div className="space-y-4">
              <h5 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{t('nav_foundation')}</h5>
              <div className="flex flex-col gap-3 text-gray-600 dark:text-gray-400">
                <Link to="/about" className="hover:text-brand-blue transition-colors">{t('nav_about')}</Link>
                <Link to="/ethics" className="hover:text-brand-blue transition-colors">{t('nav_ethics')}</Link>
              </div>
            </div>
            
            <div className="space-y-4">
              <h5 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{t('nav_guidance')}</h5>
              <div className="flex flex-col gap-3 text-gray-600 dark:text-gray-400">
                <Link to="/support" className="hover:text-brand-blue transition-colors">{t('nav_support')}</Link>
                <Link to="/crisis" className="hover:text-red-500 font-semibold transition-colors">{t('nav_crisis')}</Link>
              </div>
            </div>

            <div className="space-y-4">
              <h5 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{t('nav_integrity')}</h5>
              <div className="flex flex-col gap-3 text-gray-600 dark:text-gray-400">
                <Link to="/terms" className="hover:text-brand-blue transition-colors">{t('nav_terms')}</Link>
                <Link to="/privacy" className="hover:text-brand-blue transition-colors">{t('nav_privacy')}</Link>
                <Link to="/sustain" className="hover:text-amber-500 font-medium transition-colors">{t('nav_sustain')}</Link>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom: centered copyright */}
          <div className="text-center mt-20 pt-8 border-t border-gray-50 dark:border-gray-900 text-[10px] text-gray-400 uppercase tracking-[0.2em] flex flex-col gap-3">
          <p className="text-gray-400 dark:text-gray-500 italic mt-2">
            {t('foot_micro')}
          </p>
          <span className="opacity-40 tracking-wider">{t('foot_copy')}</span>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      <AnimatePresence>
        {showTopBtn && (
          <motion.button
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 p-3 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded-full shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-xl hover:-translate-y-1 transition-all z-50"
          >
            <ArrowUp className="w-5 h-5" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* 🌪️ VENT MODAL */}
      <AnimatePresence>
        {showVent && (
          <VentBox onClose={() => setShowVent(false)} />
        )}
      </AnimatePresence>

    </div>
  );
}
